/**
 * Goftino's live-chat widget (backend-routes.md §19), and the only file that
 * touches `window.Goftino`, the widget's script tag or its storage keys. The
 * development stub stands in for Goftino's own script, so it sits on the far
 * side of that line.
 *
 * **Goftino has no identity verification.** `setUserId(id)` is all it takes
 * to read a conversation, and the widget keeps the id in `localStorage`,
 * where it survives a reload. Anything left loaded or cached after a sign-out
 * is the previous user's financial conversation, so `teardown()` is written
 * to fail closed and to run any number of times.
 *
 * What the widget leaves, read from its loader and `client.js` (2026-10-22;
 * none of it is documented, and the script is served `no-cache`):
 *
 *  - **Globals.** `Goftino` is a top-level `var`, so it can be emptied but
 *    never deleted. `goftino_userdata` holds the last `setUser` and
 *    `goftino_widgetdata` the last `setWidget`; both survive `destroy()` and
 *    are sent again by the next init, so a guest would inherit the previous
 *    user's profile. `goftinoRemoveLoad` is a function, and `onpopstate` is
 *    overwritten by the widget's iframe.
 *  - **DOM.** The `#goftino_w` iframe, which `destroy()` removes; a
 *    `<style>` in the head, which nothing removes and every init adds again;
 *    and the image preview's transient nodes.
 *  - **Storage.** `goftino_{widget}` holds the 64-character id, and the bare
 *    `goftino` key a copy of it, which init copies back when the scoped key
 *    is missing. The rest are `goftino_*` (fingerprint, auto-messages, start
 *    form, unread, ratings) and `check_limit_after_startform`. No cookies,
 *    no `sessionStorage`, no IndexedDB.
 *  - **Listeners** the iframe adds to this window (a capture-phase click,
 *    `visibilitychange`, `resize`). Nothing holds a reference to them, so
 *    they outlive the widget until the page is reloaded.
 *
 * **`unsetUserId()` re-initialises the widget.** It removes the iframe and
 * appends a fresh loader. That loader only builds a widget while
 * `#goftino_w` is absent, so teardown leaves an inert element under that id,
 * the tombstone, and a later load removes it only once every loader script
 * that might still run has run. A script cannot be cancelled once inserted:
 * one that ran late without a tombstone would bring up a widget, and possibly
 * with the `?o=` of an identity that has since signed out.
 *
 * **The id store is shared by every tab; a widget is not.** The widget reads
 * the stored id again whenever it reconnects or reloads, so a key another tab
 * writes or removes can move this tab's chat to someone else's identity. A
 * live widget therefore lets go as soon as another tab changes the identity
 * keys, or as soon as it re-initialises without being asked to, and says so
 * through `onConflict()`. Letting go leaves the store alone: by then it
 * belongs to the other tab, and clearing it there would end that tab's chat
 * in turn. The upshot is one live chat per device at a time.
 *
 * **The widget's frame shares this origin**, so its own writes reach this
 * window as `storage` events too, from `about:srcdoc`. Those are only ever
 * the signal that Goftino issued an id; read as another tab, they tore down
 * every guest's chat and every first chat as it started. Neither they nor
 * `getUserId()` can tell tabs apart, which is why another tab's load or
 * sign-out, which clears the keys from one of this app's pages, releases this
 * widget before either tab could bind the other's id.
 *
 * **On a phone, the widget writes history.** Below 768 px its first open
 * pushes `#gchat` and replaces it with a bare `{}` at the same URL, and from
 * init its `onpopstate` closes the chat on every Back. React Router cannot
 * read a `{}` entry, and closing with the widget's own button leaves it
 * behind, so the next Back did nothing visible. The entry is rewritten as
 * React Router's own, marked, and taken back off when the chat closes; see
 * "history on phones" below.
 *
 * **What `setUser` sends is decided at connect time.** The loader only
 * stores the argument, and the widget sends it when its socket connects, so
 * a load takes the details it should connect with up front.
 */

/**
 * A Goftino visitor id: 64 characters from `[A-Za-z0-9]`. Goftino's own server
 * issues it, core only binds it, and it is compared exactly: its case is never
 * changed. Every id is checked against this before it is used — the `?o=`,
 * every value read from the widget and every answer from core.
 */
export const GOFTINO_USER_ID = /^[A-Za-z0-9]{64}$/

const WIDGET_ORIGIN = 'https://www.goftino.com/widget/'

/** Goftino may be filtered or down; a load that has not readied by then fails. */
const READY_TIMEOUT_MS = 15_000

/** How long `setUserId` may take to call back before it is reported as lost. */
const CALLBACK_TIMEOUT_MS = 10_000

/** The widget id that loads `goftinoStub.ts` in a dev build, never the network. */
const STUB_WIDGET_ID = 'sx-mock'

/** The id the loader's guard checks, and which the tombstone holds. */
const FRAME_ID = 'goftino_w'

/**
 * The one key this module writes itself, from the page, as every load
 * starts. Clearing the cached ids only reaches another tab when a key was
 * there to remove, and a widget still downloading has written none, so two
 * tabs loading within a second of each other would both stay up on one id
 * store. This write always reaches the other tab, which lets its widget go.
 * It is a `goftino_*` key, so every clearing removes it with the rest.
 */
const CLAIM_KEY = 'goftino_sx_claim'

/** The image preview's nodes, appended to this document while one is open. */
const PREVIEW_IDS = [
  'goftino_image_fullscreen',
  'goftino_loading_img',
  'goftino_close_screen',
  'goftino_dl_image',
]

export interface GoftinoMetadata {
  key: string
  value: string
}

/**
 * `setUser`'s argument. The fields are whatever core's `profile` carries,
 * passed through as sent; only `metadata` and `forceUpdate` are this app's.
 */
export interface GoftinoUser {
  [field: string]: unknown
  metadata?: GoftinoMetadata[]
  forceUpdate?: boolean
}

/** `setWidget`'s options. `counter` is never used: it writes into React's DOM. */
export interface GoftinoWidget {
  hasIcon?: boolean
  hasSound?: boolean
}

/** `setUserId`'s callback. `comment` is Goftino's English reason. */
export interface GoftinoResult {
  status: 'success' | 'error'
  comment?: string
}

/** `goftino_getMessage`'s detail: an operator's message, never an automatic one. */
export interface GoftinoIncoming {
  type: 'text' | 'file' | 'voice' | 'question'
  content: unknown
}

/** The methods this app calls. The loader defines more. */
export interface GoftinoApi {
  open(): void
  close(): void
  setUserId(id: string, callback?: (result: GoftinoResult) => void): void
  /** The id stored for this visitor, or `''` before Goftino has issued one. */
  getUserId(): string
  unsetUserId(): void
  setUser(user: GoftinoUser): void
  setWidget(options: GoftinoWidget): void
  sendMessage(message: { text: string }): void
  destroy(): void
}

/** The globals the widget defines on this window. */
export interface GoftinoGlobals {
  Goftino?: GoftinoApi
  goftino_userdata?: GoftinoUser
  goftino_widgetdata?: GoftinoWidget
  goftinoRemoveLoad?: () => void
}

const GLOBALS: (keyof GoftinoGlobals)[] = [
  'Goftino',
  'goftino_userdata',
  'goftino_widgetdata',
  'goftinoRemoveLoad',
]

/** A load that failed: Goftino timed out, was unreachable, or was refused. */
export class GoftinoLoadError extends Error {
  constructor(reason: string) {
    super(`support chat did not load: ${reason}`)
    this.name = 'GoftinoLoadError'
  }
}

function host(): Window & GoftinoGlobals {
  return window as Window & GoftinoGlobals
}

/* --- storage -------------------------------------------------------------- */

function isGoftinoKey(key: string): boolean {
  return key === 'goftino' || key.startsWith('goftino_') || key === 'check_limit_after_startform'
}

/**
 * Removes every key the widget writes, and nothing else. For when nothing is
 * loaded: a page that ended without a teardown leaves the id in storage, and
 * the next load would otherwise resume it.
 */
export function clearCachedIds(): void {
  try {
    const storage = globalThis.localStorage
    if (!storage) return
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key !== null && isGoftinoKey(key)) keys.push(key)
    }
    for (const key of keys) storage.removeItem(key)
  } catch {
    /* private mode — nothing was kept to remove */
  }
}

/* --- loader scripts --------------------------------------------------------
 * Every loader script that is ever inserted is tracked until it has run or
 * failed, whoever inserted it: this module, `unsetUserId()`, or the widget's
 * own reload. Insertions are seen by an observer, so a script is tracked
 * before it can run, and a load waits for every one of them to settle. */

const settled = new WeakMap<HTMLScriptElement, Promise<void>>()
const unsettled = new Set<HTMLScriptElement>()
let observer: MutationObserver | null = null

function isLoaderScript(node: Node): node is HTMLScriptElement {
  if (!(node instanceof HTMLScriptElement)) return false
  if (node.src.startsWith(WIDGET_ORIGIN)) return true
  // The stub's stand-in for the loader `unsetUserId()` appends; dev only.
  return import.meta.env.DEV && node.dataset.sxGoftinoStub !== undefined
}

function track(script: HTMLScriptElement): void {
  if (settled.has(script)) return
  unsettled.add(script)
  settled.set(
    script,
    new Promise<void>((resolve) => {
      const done = () => {
        unsettled.delete(script)
        resolve()
      }
      script.addEventListener('load', done, { once: true })
      script.addEventListener('error', done, { once: true })
    }),
  )
}

function trackRecords(records: MutationRecord[]): void {
  for (const record of records) {
    for (const node of record.addedNodes) if (isLoaderScript(node)) track(node)
  }
}

function watchScripts(): void {
  if (observer || typeof MutationObserver === 'undefined') return
  observer = new MutationObserver(trackRecords)
  observer.observe(document.head, { childList: true })
  observer.observe(document.body, { childList: true })
}

/** Resolves once no tracked loader can still run, or rejects at `deadline`. */
function scriptsSettled(deadline: number): Promise<void> {
  if (unsettled.size === 0) return Promise.resolve()
  const pending = [...unsettled].map((script) => settled.get(script))
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new GoftinoLoadError('an earlier loader is still in flight')),
      Math.max(0, deadline - Date.now()),
    )
    void Promise.all(pending).then(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

function placeTombstone(): void {
  const existing = document.getElementById(FRAME_ID)
  if (existing instanceof HTMLTemplateElement) return
  existing?.remove()
  const tombstone = document.createElement('template')
  tombstone.id = FRAME_ID
  document.head.append(tombstone)
}

function removeTombstone(): void {
  const existing = document.getElementById(FRAME_ID)
  if (existing instanceof HTMLTemplateElement) existing.remove()
}

/* --- load ----------------------------------------------------------------- */

interface Pending {
  key: string
  promise: Promise<void>
  abort: (error: Error) => void
}

/** `idle` → `loading` → `ready`, and back to `idle` on any teardown. */
let phase: 'idle' | 'loading' | 'ready' = 'idle'
/** The load in flight or done, shared by every caller that asks for the same one. */
let current: Pending | null = null
/** Bumped by every teardown, so work a teardown overtook can tell. */
let generation = 0
/** `onpopstate` before the widget overwrote it. */
let savedPopstate: Window['onpopstate'] | undefined
/** The identity loaded or loading, to tell another tab's writes from ours. */
let loadedTarget: LoadTarget | null = null
/** The `goftino_ready` a load resolved on, which the stray watcher must skip. */
let claimedReady: Event | null = null
const conflictListeners = new Set<() => void>()

export interface LoadTarget {
  widgetId: string
  /**
   * The signed-in account the widget is for, by `/me`'s id; `null` for a
   * guest. An unbound widget has no Goftino id to tell it apart by, so a
   * guest and an unbound user would otherwise be the same load.
   */
  account: string | null
  /** The account's bound Goftino id; `null` for a guest or before binding. */
  userId: string | null
  /**
   * For an unbound account: called with the id Goftino issues this visitor,
   * once per distinct id, until `markBound()`. It is armed before the loader
   * is injected, because the id may arrive as the widget connects.
   */
  onWidgetId?: (id: string) => void
  /** Applied before the widget draws, so the bubble never flashes. */
  widget?: GoftinoWidget
  /** What the first connect sends, as `setUser` would have stored it. */
  user?: GoftinoUser
  /**
   * Epoch milliseconds the load must be ready by, when the caller's own
   * budget ends before this module's 15 seconds would.
   */
  deadline?: number
}

/**
 * Loads the widget for one identity, at most once at a time.
 *
 * It clears every cached id first, so a guest never resumes a previous
 * user's conversation and a signed-in load never starts from someone else's
 * id. A signed-in load carries the id as `?o=`, Goftino's own resume
 * parameter, so the first paint is already that user's.
 *
 * Resolves on `goftino_ready`; rejects after 15 seconds, on a script error, or
 * when a teardown overtakes it. A failed load is torn down before it rejects.
 * A second call for the same identity shares the first; a call for another
 * identity is refused, because the caller must tear the first down.
 */
export function load(target: LoadTarget): Promise<void> {
  const key = `${target.widgetId}\n${target.account ?? ''}\n${target.userId ?? ''}`
  if (current) {
    return current.key === key
      ? current.promise
      : Promise.reject(new GoftinoLoadError('another identity is loaded'))
  }
  if (target.userId !== null && !GOFTINO_USER_ID.test(target.userId)) {
    return Promise.reject(new GoftinoLoadError('malformed user id'))
  }
  if (target.widgetId === '') return Promise.reject(new GoftinoLoadError('no widget id'))

  const token = ++generation
  const deadline = Math.min(Date.now() + READY_TIMEOUT_MS, target.deadline ?? Infinity)
  let finish: (error?: Error) => void = () => undefined

  const promise = new Promise<void>((resolve, reject) => {
    const onReady = (event: Event) => {
      if (token !== generation || phase !== 'loading') return
      claimedReady = event
      finish()
    }
    const timer = setTimeout(
      () => fail(new GoftinoLoadError('timed out')),
      Math.max(0, deadline - Date.now()),
    )
    finish = (error) => {
      clearTimeout(timer)
      window.removeEventListener('goftino_ready', onReady)
      if (error) {
        reject(error)
      } else {
        phase = 'ready'
        pollWidgetId()
        resolve()
      }
    }
    window.addEventListener('goftino_ready', onReady)
  })

  function fail(error: Error) {
    if (token !== generation) return
    finish(error)
    // Nothing half-loaded stays behind a failure, and a late script is inert.
    teardown()
  }

  current = { key, promise, abort: (error) => finish(error) }
  phase = 'loading'
  loadedTarget = target
  idWatch =
    target.userId === null && target.onWidgetId
      ? { report: target.onWidgetId, last: null, timer: null }
      : null
  watchWidgetIds()
  watchHistory()
  addsHistory = false
  watchStrayReady()
  watchOtherTabs()
  watchScripts()
  void start(token, target, deadline, fail)
  return promise
}

async function start(
  token: number,
  target: LoadTarget,
  deadline: number,
  fail: (error: Error) => void,
): Promise<void> {
  clearCachedIds()
  claimStore()
  clearGlobals()
  // `setWidget` only stores its argument for the widget to read, so storing
  // it first is the same call made before anything can draw.
  if (target.widget) host().goftino_widgetdata = { ...target.widget }
  // The same for `setUser`: the widget sends it when it connects, and a call
  // made after that waits for the next reconnect.
  if (target.user) host().goftino_userdata = { ...target.user }

  try {
    await scriptsSettled(deadline)
  } catch (error) {
    fail(error as Error)
    return
  }
  if (token !== generation) return
  removeTombstone()
  savedPopstate = window.onpopstate

  if (target.widgetId === STUB_WIDGET_ID) {
    if (import.meta.env.DEV) {
      try {
        const { installGoftinoStub } = await import('./goftinoStub')
        if (token !== generation) return
        installGoftinoStub(target.widgetId, target.userId)
      } catch {
        fail(new GoftinoLoadError('the stub did not install'))
      }
      return
    }
    // A build that still runs the fixtures must not send theirs to Goftino.
    fail(new GoftinoLoadError('the stub widget loads in development only'))
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src =
    WIDGET_ORIGIN +
    encodeURIComponent(target.widgetId) +
    (target.userId === null ? '' : `?o=${target.userId}`)
  track(script)
  script.addEventListener('error', () => fail(new GoftinoLoadError('the script failed')), {
    once: true,
  })
  document.head.append(script)
}

/**
 * A `goftino_ready` this module did not ask for. While idle it is a script
 * the tombstone should have kept inert; while ready it is the widget
 * reloading itself — an operator's remote reload, or its stored id no longer
 * matching its own — and taking its identity from the shared store. Either
 * way it is let go.
 */
let strayReadyWatched = false
function watchStrayReady(): void {
  if (strayReadyWatched) return
  strayReadyWatched = true
  window.addEventListener('goftino_ready', (event) => {
    if (event === claimedReady || phase === 'loading') return
    conflict()
  })
}

function isIdentityKey(key: string, widgetId: string): boolean {
  return (
    key === 'goftino' ||
    key === CLAIM_KEY ||
    key === `goftino_${widgetId}` ||
    key === `goftino_${widgetId}_fp`
  )
}

function claimStore(): void {
  try {
    globalThis.localStorage?.setItem(
      CLAIM_KEY,
      globalThis.crypto?.randomUUID?.() ?? String(Math.random()),
    )
  } catch {
    /* private mode — there is no shared store to claim */
  }
}

/**
 * Another of this app's pages wrote or removed an identity key, `_fp`
 * included: it signed out, signed in, opened a chat of its own or cleared the
 * ids, while this widget may not even have an id yet. Only a write of this
 * same user's id is left alone, which a second tab of the same user makes.
 * The widget's own frame is never another tab: its writes are the id signal.
 */
let otherTabsWatched = false
function watchOtherTabs(): void {
  if (otherTabsWatched) return
  otherTabsWatched = true
  window.addEventListener('storage', (event) => {
    const target = loadedTarget
    if (phase === 'idle' || !target) return
    try {
      if (event.storageArea !== globalThis.localStorage) return
    } catch {
      return
    }
    if (fromWidgetFrame(event)) return
    if (event.key !== null && !isIdentityKey(event.key, target.widgetId)) return
    if (target.userId !== null && event.newValue === target.userId) return
    conflict()
  })
}

/* --- the widget's own id --------------------------------------------------
 * A new visitor starts with only a fingerprint. Goftino's server issues the
 * 64-character id at connect, or with the answer to the first message —
 * its call — and the widget writes it to `goftino_{widget}` and the bare
 * `goftino` from its own same-origin frame. That write is the signal, and it
 * reaches this window as a `storage` event. `getUserId()` is read too — at
 * ready, after every message the visitor sends, and once a second — in case
 * the event was missed. */

interface IdWatch {
  report: (id: string) => void
  last: string | null
  timer: ReturnType<typeof setInterval> | null
}

let idWatch: IdWatch | null = null

/**
 * A write by the widget's frame (`about:srcdoc`), or by anything else that is
 * not one of this app's pages, as opposed to another tab of this app.
 */
function fromWidgetFrame(event: StorageEvent): boolean {
  try {
    return new URL(event.url).origin !== globalThis.location.origin
  } catch {
    return true
  }
}

function reportWidgetId(id: string | null): void {
  const watch = idWatch
  if (!watch || id === null || !GOFTINO_USER_ID.test(id) || id === watch.last) return
  watch.last = id
  try {
    watch.report(id)
  } catch {
    /* the caller's bind reports its own failures */
  }
}

function readWidgetId(): void {
  if (!idWatch || phase !== 'ready') return
  try {
    reportWidgetId(getUserId())
  } catch {
    /* the widget is going */
  }
}

let widgetIdsWatched = false
function watchWidgetIds(): void {
  if (widgetIdsWatched) return
  widgetIdsWatched = true
  window.addEventListener('storage', (event) => {
    const target = loadedTarget
    if (!idWatch || !target || !fromWidgetFrame(event)) return
    try {
      if (event.storageArea !== globalThis.localStorage) return
    } catch {
      return
    }
    if (event.key !== 'goftino' && event.key !== `goftino_${target.widgetId}`) return
    reportWidgetId(event.newValue)
  })
  window.addEventListener('goftino_sendMessage', readWidgetId)
}

/** At ready: read once, then once a second until bound or torn down. */
function pollWidgetId(): void {
  const watch = idWatch
  if (!watch) return
  readWidgetId()
  watch.timer ??= setInterval(readWidgetId, 1000)
}

function stopIdWatch(): void {
  if (idWatch?.timer) clearInterval(idWatch.timer)
  idWatch = null
}

function conflict(): void {
  // Another tab's doing, not a navigation in this one.
  leaveChatEntry()
  dismantle(false)
  for (const listener of conflictListeners) listener()
}

/**
 * Called after this module let the widget go on its own, because its
 * identity may no longer be the one loaded. The caller drops its own state;
 * the widget is already gone.
 */
export function onConflict(listener: () => void): () => void {
  conflictListeners.add(listener)
  return () => {
    conflictListeners.delete(listener)
  }
}

/* --- teardown ------------------------------------------------------------- */

function clearGlobals(): void {
  const target = host()
  for (const name of GLOBALS) {
    try {
      // `var Goftino` cannot be deleted, and a strict-mode delete throws.
      delete target[name]
    } catch {
      /* non-configurable — emptied below */
    }
    try {
      if (name in target) target[name] = undefined
    } catch {
      /* read-only — nothing more can be done from here */
    }
  }
}

function attempt(step: () => void): void {
  try {
    step()
  } catch {
    /* teardown keeps going: every later step still has to run */
  }
}

/**
 * Takes the widget down and removes everything it left. Idempotent and never
 * throws: a failed refresh clears the tokens twice, and every step has to run
 * whatever the one before it did.
 *
 *  1. When the widget exists, `unsetUserId()` and then `destroy()`.
 *  2. The loader scripts, the iframe, the widget's stylesheet and preview
 *     nodes go; the tombstone goes in; the globals are emptied and
 *     `onpopstate` is put back.
 *  3. Every key the widget writes is removed.
 *  4. The load state resets, and a load in flight rejects.
 */
export function teardown(): void {
  dismantle(true)
}

/**
 * Teardown, or with `ownStore` false, letting go of a widget whose store
 * another tab now owns: no `unsetUserId()`, which clears the store, and no
 * clearing of it either.
 */
function dismantle(ownStore: boolean): void {
  generation += 1
  const pending = current
  current = null
  phase = 'idle'
  loadedTarget = null
  stopIdWatch()

  const goftino = host().Goftino
  if (goftino) {
    if (ownStore) attempt(() => goftino.unsetUserId())
    // `unsetUserId()` has just appended a loader; track it before it can run.
    attempt(() => {
      if (observer) trackRecords(observer.takeRecords())
    })
    attempt(() => goftino.destroy())
  }

  attempt(() => {
    for (const script of document.querySelectorAll('script')) {
      if (isLoaderScript(script)) script.remove()
    }
  })
  attempt(() => {
    const frame = document.getElementById(FRAME_ID)
    if (frame && !(frame instanceof HTMLTemplateElement)) frame.remove()
  })
  attempt(() => {
    for (const style of document.querySelectorAll('style')) {
      if (style.textContent?.includes(`#${FRAME_ID}{`)) style.remove()
    }
  })
  attempt(() => {
    for (const id of PREVIEW_IDS) document.getElementById(id)?.remove()
  })
  attempt(placeTombstone)
  attempt(clearGlobals)
  attempt(() => {
    if (savedPopstate !== undefined) window.onpopstate = savedPopstate
    savedPopstate = undefined
  })
  // The widget's script URL carries `?o=` with the id, and the page keeps
  // every URL it fetched in its resource timings until it is reloaded.
  attempt(() => performance.clearResourceTimings())
  attempt(unmarkChatEntry)

  if (ownStore) clearCachedIds()

  pending?.abort(new GoftinoLoadError('torn down'))
}

/* --- the widget's API ----------------------------------------------------- */

function widget(): GoftinoApi {
  const goftino = phase === 'ready' ? host().Goftino : undefined
  if (!goftino) throw new GoftinoLoadError('not loaded')
  return goftino
}

export function open(): void {
  const goftino = widget()
  const state: unknown = history.state
  if (isRouterEntry(state) && !isChatEntry(state)) {
    routerEntry = { state, href: location.href }
  }
  goftino.open()
}

export function close(): void {
  widget().close()
}

/** Goftino's answer when the stored id is already the one `setUserId` sends. */
const ALREADY_SET = 'This userid is already set on widget'

/**
 * What `setUserId` came to: `applied`, which includes «already set» because
 * this module's own `?o=` stored it; `refused`, whatever Goftino's reason —
 * «Not done, try again later» for an id its server does not know, «Userid is
 * invalid…» for a wrong length; `silent`, no callback at all, which is how a
 * network failure shows; or `stale`, an answer that came after the widget it
 * was asked of had been torn down or reloaded, which is no answer at all.
 */
export type SetUserIdOutcome = 'applied' | 'refused' | 'silent' | 'stale'

/**
 * Goftino's documented way to resume an id. It answers through a callback,
 * which never comes if the widget or the network is gone, so the wait is
 * bounded.
 */
export function setUserId(id: string): Promise<SetUserIdOutcome> {
  const goftino = widget()
  const asked = generation
  return new Promise((resolve) => {
    const answer = (outcome: SetUserIdOutcome) => resolve(asked === generation ? outcome : 'stale')
    const timer = setTimeout(() => answer('silent'), CALLBACK_TIMEOUT_MS)
    goftino.setUserId(id, (result) => {
      clearTimeout(timer)
      // «Already set» is read from the stored id too, in case Goftino ever
      // rewords it: a refusal read wrongly would retire a live identity.
      const applied =
        result.status === 'success' || result.comment === ALREADY_SET || storedIdIs(id)
      answer(applied ? 'applied' : 'refused')
    })
  })
}

function storedIdIs(id: string): boolean {
  try {
    return widget().getUserId() === id
  } catch {
    return false
  }
}

/** The id Goftino has issued this visitor, or `null` while it has none. */
export function getUserId(): string | null {
  const id = widget().getUserId()
  return GOFTINO_USER_ID.test(id) ? id : null
}

/**
 * The id is bound: stop watching for one, since a later reload writes the
 * key again, and let the cross-tab rules compare against it.
 */
export function markBound(id: string): void {
  stopIdWatch()
  if (loadedTarget && loadedTarget.userId === null) {
    loadedTarget = { ...loadedTarget, userId: id }
    if (current) {
      current.key = `${loadedTarget.widgetId}\n${loadedTarget.account ?? ''}\n${id}`
    }
  }
}

export function unsetUserId(): void {
  widget().unsetUserId()
}

export function setUser(user: GoftinoUser): void {
  widget().setUser(user)
}

export function setWidget(options: GoftinoWidget): void {
  widget().setWidget(options)
}

/** A note shown to the user only; Goftino does not add it to the conversation. */
export function sendMessage(message: { text: string }): void {
  widget().sendMessage(message)
}

export function destroy(): void {
  widget().destroy()
}

/* --- history on phones ----------------------------------------------------
 * Below 768 px the widget's first open in an instance runs
 * `pushState({}, null, "#gchat")` and then `replaceState({}, null, href)`:
 * a new top entry at the same URL whose state React Router cannot read, so
 * it took the entry for a cold load. Its `onpopstate`, set at init, closes
 * the chat on every Back once that has happened, and it never pushes again;
 * closing with its own button never touches history.
 *
 * So the entry is made React Router's: on `goftino_openWidget` after the
 * widget's push, the bare `{}` becomes a copy of the router's state from
 * just before the open, marked; on a later open of the same instance, this
 * module pushes that marked copy itself, so Back has an entry to pop. A
 * Back while the chat is open pops the mark and lands on the same screen,
 * and the widget closes the chat. A close that was not a Back goes back
 * once per mark, only while the marked entry is current, and the close
 * that back raises finds it gone. A teardown never navigates, since it may
 * be part of a navigation already under way: it only un-marks a current
 * mark in place. Only the background's own endings — a conflict, a bind
 * given up, a reload — go back off it first. */

const CHAT_ENTRY = 'sxGoftinoChat'

/** React Router's state from just before this module's last `open()`, and where. */
let routerEntry: { state: object; href: string } | null = null
/** Whether this widget instance has pushed its own entry once already. */
let addsHistory = false
/**
 * Whether the current entry is a mark this module made and has not taken
 * back yet, so a close goes back once per mark and never further.
 */
let marked = false

/**
 * React Router's own entry. It always carries `idx`, and `key` too except on
 * the entry a cold load starts from, which is `{ idx: 0 }`; the widget's push
 * carries neither.
 */
function isRouterEntry(state: unknown): state is { idx: number } {
  return (
    !!state &&
    typeof state === 'object' &&
    'idx' in state &&
    typeof (state as { idx: unknown }).idx === 'number'
  )
}

function isChatEntry(state: unknown): boolean {
  return !!state && typeof state === 'object' && CHAT_ENTRY in state
}

function onChatOpened(): void {
  try {
    const state: unknown = history.state
    if (!isRouterEntry(state)) {
      // The widget has just pushed. Its replace kept the URL, so the copy
      // is only ever taken from the same screen; a remote open from an
      // operator elsewhere is left as the widget made it.
      if (routerEntry && routerEntry.href === location.href) {
        history.replaceState({ ...routerEntry.state, [CHAT_ENTRY]: true }, '')
        addsHistory = true
        marked = true
      }
    } else if (addsHistory && !isChatEntry(state)) {
      history.pushState({ ...state, [CHAT_ENTRY]: true }, '')
      marked = true
    }
  } catch {
    /* history is the router's to keep working; this is a nicety */
  }
}

function onChatClosed(): void {
  leaveChatEntry()
}

/**
 * Takes the chat's marked entry back off history when it is current. For a
 * chat that closes, and for one the background ends — never for a teardown
 * that may be part of a navigation already under way.
 */
export function leaveChatEntry(): void {
  try {
    if (marked && isChatEntry(history.state)) {
      marked = false
      history.back()
    }
  } catch {
    /* history is the router's to keep working; this is a nicety */
  }
}

/**
 * On a teardown: a mark still current is made a plain copy of the router's
 * entry, without navigating, so it cannot pile up under the next widget's
 * and send a later close back past the screen. The extra entry stays, as a
 * Back that changes nothing visible.
 */
function unmarkChatEntry(): void {
  marked = false
  try {
    const state: unknown = history.state
    if (!isChatEntry(state)) return
    const plain = { ...(state as Record<string, unknown>) }
    delete plain[CHAT_ENTRY]
    history.replaceState(plain, '')
  } catch {
    /* as above */
  }
}

let historyWatched = false
function watchHistory(): void {
  if (historyWatched) return
  historyWatched = true
  window.addEventListener('goftino_openWidget', onChatOpened)
  window.addEventListener('goftino_closeWidget', onChatClosed)
}

/* --- events ----------------------------------------------------------------
 * Goftino dispatches these on this window, and again after every re-init. An
 * operator can open and close the panel remotely, which fires them too. */

export function onWidgetOpen(listener: () => void): () => void {
  window.addEventListener('goftino_openWidget', listener)
  return () => window.removeEventListener('goftino_openWidget', listener)
}

export function onWidgetClose(listener: () => void): () => void {
  window.addEventListener('goftino_closeWidget', listener)
  return () => window.removeEventListener('goftino_closeWidget', listener)
}

export function onMessage(listener: (message: GoftinoIncoming) => void): () => void {
  const handler = (event: Event) => {
    listener((event as CustomEvent<GoftinoIncoming>).detail)
  }
  window.addEventListener('goftino_getMessage', handler)
  return () => window.removeEventListener('goftino_getMessage', handler)
}
